import { h, defineComponent, ref, SetupContext } from 'vue';
import { ValidationError } from '@/entities/validation';
// @ts-ignore
import { BDatepicker } from 'buefy/dist/esm/datepicker';
import { d, t } from '@/ui/i18n/i18n';
import { DateRange, SetterCallback } from '@/ui/types';
import { Field, FieldProps } from './Field';
import { Button } from '../Button';
import { user } from '@/ui/auth/auth';

export interface DateRangeFieldProps {
  name: string;
  label: string;
  value: DateRange | null;
  required: boolean;
  placeholder: string;
  errors: ValidationError[];
  clearable: boolean;
  disabled: boolean;
  rangeLimitMonths?: number;
  hideLabel?: boolean;
  horizontal?: boolean;
  unselectableDates?: (date: Date) => boolean;
}

const today = new Date();
const defaultMinDate = new Date(today.getFullYear() - 20, today.getMonth(), today.getDate());
const defaultMaxDate = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());

export const DateRangeField = defineComponent<DateRangeFieldProps>({
  name: 'DateRangeField',
  props: [
    'name', 'label', 'value', 'required', 'placeholder', 'errors', 'clearable', 'rangeLimitMonths',
    'hideLabel', 'horizontal', 'disabled', 'unselectableDates',
  ],
  setup(props, context: SetupContext) {
    const getMaxDate = () => {
      return props.rangeLimitMonths
        ? new Date(today.getFullYear(), today.getMonth() + props.rangeLimitMonths, today.getDate())
        : defaultMaxDate;
    };

    const minDate = ref<Date>(defaultMinDate);
    const maxDate = ref<Date>(getMaxDate());

    return function render() {
      const fieldProps: FieldProps = {
        label: props.label,
        name: props.name,
        required: props.required,
        errors: props.errors || [],
        hideLabel: props.hideLabel,
        horizontal: props.horizontal,
      };

      return h(Field, { props: fieldProps, class: 'date-range-field' }, [
        h(BDatepicker, {
          props: {
            class: 'date-range-picker',
            value: props.value,
            range: true,
            locale: user.value.locale,
            icon: 'calendar',
            placeholder: props.placeholder,
            yearsRange: [-3, 3],
            //1 - Monday, 0 - Sunday
            firstDayOfWeek: 1,
            // max 3 months selection from today, but we need to find a way to select from minDate
            minDate: minDate.value,
            maxDate: maxDate.value,
            unselectableDates: props.unselectableDates,
          },
          attrs: { disabled: props.disabled },
          on: {
            input: (dateRange: DateRange) => {
              context.emit('input', dateRange);
            },
            'range-start': (date: Date) => {
              context.emit('range-start', date);
            },
            'range-end': (date: Date) => {
              context.emit('range-end', date);
            },
          },
        }, [
          props.clearable && props.value !== null
            ? h(Button, {
              props: { type: 'is-light' },
              on: {
                click: () => {
                  context.emit('input', [null, null]);
                },
              },
            }, t('form.clear-dates'))
            : null,
        ]),
      ]);
    };
  },
});

export function dateRangeField(props: DateRangeFieldProps, input: SetterCallback<DateRange>) {
  return h(DateRangeField, {
    props,
    on: { input },
  });
}
